import { Dialog } from 'quasar'
import ErrorDialog from '../ErrorDialog.vue'
import type { ErrorDialogConfig } from '../types'

/**
 * Shows an error dialog using Quasar Dialog API with the custom ErrorDialog component
 * @public
 * @param config - Dialog configuration with error and optional context
 * @returns Promise that resolves when dialog is closed
 *
 * @example
 * ```typescript
 * import { showErrorDialog } from 'src/CrudManager/Core/composites/Dialogs/ErrorDialog'
 *
 * try {
 *   await apiCall()
 * } catch (error) {
 *   await showErrorDialog({
 *     error: error as Error,
 *     modelName: 'User',
 *     instanceId: '123',
 *     onRetry: async () => {
 *       await apiCall() // Retry the operation
 *     }
 *   })
 * }
 * ```
 *
 * @useWhen need to display API errors programmatically; want contextual error handling with suggestions; need retry functionality for recoverable errors
 */
export function showErrorDialog(config: ErrorDialogConfig): Promise<void> {
  return new Promise((resolve) => {
    const dialog = Dialog.create({
      component: ErrorDialog,
      componentProps: {
        error: config.error,
        modelName: config.modelName,
        instanceId: config.instanceId,
        onRetry: config.onRetry,
      },
      persistent: true,
    })

    dialog
      .onOk(() => resolve())
      .onCancel(() => resolve())
      .onDismiss(() => resolve())
  })
}









